/**
 * "Phira will play this differently without info.yml" (W2-09 publish-import; DESIGN.md §4.16 "Chart file only /
 * [Copy chart.json]", §5.11 step 2; ARCHITECTURE.md §11): the package switches a bare chart.json cannot carry and,
 * for RPE 1.7 speed projects, "Bake speed to linear for this file".
 *
 *  - 'target': the export target is the chart file alone; always shown (it also says when nothing changes).
 *  - 'copy': "Copy chart.json" next to another target (the Export dialog and the Publish workspace offer it whatever
 *    the target); shown when there is something to say, so the list and the bake are in front of the user before
 *    anything is copied.
 *
 * The list is computed from the document at once (no waiting for the export's lowering), with the bake preference
 * the copy and the chart-file export use.
 */

import { useId } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Checkbox } from '../../ui/Checkbox';
import { Icon } from '../../ui/Icon';
import { chartFileSwitches } from './exportFlow';
import type { Text } from './hooks';
import type { PublishModel } from './model';
import { writePref } from './prefs';
import { m } from './publish.messages';
import s from './Publish.module.css';

export interface ChartOnlyNoticeProps {
  readonly services: Pick<AppServices, 'settings'>;
  readonly text: Text;
  readonly model: Pick<PublishModel, 'store' | 'prefs' | 'conversion'>;
  readonly mode: 'target' | 'copy';
  /**
   * Folded under its title (the Export dialog with a package target: the note is about "Copy chart.json" only and
   * stays out of the way of the export itself, UX-077).
   */
  readonly collapsed?: boolean;
}

export function ChartOnlyNotice(props: ChartOnlyNoticeProps) {
  const { services, text, model, mode } = props;
  const id = useId();
  const doc = model.store.doc;
  const rpe170 = doc.pkg.speedMode === 'rpe170';
  const switches = chartFileSwitches(doc, model.prefs.bakeSpeedForChartOnly, model.conversion?.lowered ?? null);
  if (mode === 'copy' && switches.length === 0 && !rpe170) return null;
  const title = text.t(mode === 'copy' ? m.copySwitchesTitle : m.switchesTitle);
  const body = (
    <>
      {switches.length === 0 ? (
        <span>{text.t(mode === 'copy' ? m.copyNoSwitches : m.noSwitches)}</span>
      ) : (
        <ul className={s.noteList} aria-label={title}>
          {switches.map((sw, i) => (
            <li key={`${sw.key}:${String(i)}`}>{text.tr(sw)}</li>
          ))}
        </ul>
      )}
      {rpe170 && (
        <>
          <Checkbox
            label={text.t(m.bakeSpeed)}
            checked={model.prefs.bakeSpeedForChartOnly}
            onChange={(v) => {
              writePref(services.settings, 'bakeSpeedForChartOnly', v);
            }}
          />
          <span className={s.muted}>{text.t(m.bakeSpeedHelp)}</span>
        </>
      )}
    </>
  );
  if (props.collapsed === true) {
    return (
      <details className={s.disclosure} data-notice={mode}>
        <summary>
          <Icon name={switches.length > 0 ? 'TriangleAlert' : 'Info'} size={14} />
          {text.t(m.copyNoticeSummary, { count: switches.length })}
        </summary>
        <div className={s.disclosureBody}>{body}</div>
      </details>
    );
  }
  return (
    <section className={s.warning} aria-labelledby={id} data-notice={mode}>
      <Icon name={switches.length > 0 ? 'TriangleAlert' : 'Info'} size={16} />
      <div className={s.noticeBody}>
        <h3 id={id} className={s.sectionLabel}>
          {title}
        </h3>
        {body}
      </div>
    </section>
  );
}
